import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Platform, Pressable, TextInput, View } from "react-native";
import { Phone, X, type LucideIcon } from "lucide-react-native";
import {
  AsYouType,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import { colors } from "../theme/tokens";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";
import { MoonPhoneCountryPicker } from "./MoonPhoneCountryPicker";
import { MoonPhoneCountrySelector } from "./MoonPhoneCountrySelector";
import { useInputFontSize } from "../utils/inputFontSize";
import { findMoonPhoneCountry } from "../phoneCountries";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Inline phone-number input. Direct typing, no modal.
 *
 * Product feedback (2026-05): the old modal-based PhoneEditField added a
 * tap-to-open step + a tap-to-save step. Users wanted "just type the
 * phone number" — so this primitive is a plain inline TextInput that
 * formats as you type via libphonenumber's AsYouType, validates via
 * isValidPhoneNumber, and emits E.164 through onChange.
 *
 * The dial-code prefix is a tappable button. Tapping it expands an inline
 * country picker below the field (filterable scroll list) — not a
 * separate modal. Pasting an international number with a leading `+`
 * also re-routes the country automatically.
 *
 * `value` is the canonical E.164 string (`+15551234567`) or `""`.
 */
type Props = {
  label?: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  /** Canonical E.164 phone number, e.g. "+15551234567". "" for empty. */
  value: string;
  onChange: (next: string) => void;
  /** Notifies the caller when the active country changes from a selector or paste. */
  onCountryChange?: (country: CountryCode) => void;
  /** Optional national-digit observer for flows that persist digits separately. */
  onNationalChange?: (digits: string, country: CountryCode) => void;
  /** ISO-3166-1 alpha-2 country code. Defaults to "US". */
  defaultCountry?: CountryCode;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
  /**
   * Overrides the small leading icon rendered inside the input, to the
   * left of the dial-code button. Defaults to a lucide `Phone`. Accepts
   * any node (lucide icon, themed Image, etc.) so callers can match the
   * surrounding iconography. Pass `null` to suppress the icon entirely.
   */
  icon?: ReactNode;
  /**
   * Optional trailing slot rendered to the right of the phone input,
   * inside the same field row. Use this for a verification badge or any
   * adornment that belongs with the value (e.g. a verification control).
   */
  right?: ReactNode;
  /**
   * When provided, renders a small clear (X) button trailing the value.
   * Invoked on press. Hidden automatically when the field is empty so the
   * affordance only appears when there is something to clear.
   */
  onClear?: () => void;
  /** Matches the dark glass controls used on warm, image-backed surfaces. */
  appearance?: "default" | "glass";
  /** Controls whether the country selector is rendered inside the field. */
  countrySelector?: "visible" | "hidden";
  countrySelectorTestID?: string;
  countrySearchTestID?: string;
  countrySearchPlaceholder?: string;
  countryNoMatchesMessage?: (query: string) => string;
};

type InitialPhoneValue = {
  country: CountryCode;
  national: string;
  parsed: boolean;
  /** Canonical E.164 form of a fully-parsed value, e.g. "+12125550123". */
  e164?: string;
  partialInternational?: string;
};

function parseInitial(
  value: string,
  fallbackCountry: CountryCode,
): InitialPhoneValue {
  if (!value) {
    return { country: fallbackCountry, national: "", parsed: false };
  }
  const p = parsePhoneNumberFromString(value);
  if (p?.country) {
    return {
      country: p.country,
      national: p.nationalNumber,
      parsed: true,
      e164: p.number,
    };
  }
  const partialInternational = value.match(/^\+[\d\s().-]*$/)
    ? value.replace(/[^\d+]/g, "")
    : undefined;
  // Partial / invalid value (e.g. "+336" while the user is mid-typing for
  // France). Keep the raw digits but flag `parsed: false` so the effect
  // below won't override the user's currently-selected country.
  return {
    country: fallbackCountry,
    national: partialInternational ? "" : value.replace(/[^\d]/g, ""),
    parsed: false,
    partialInternational,
  };
}

export function MoonPhoneField({
  label,
  labelIcon,
  value,
  onChange,
  onCountryChange,
  onNationalChange,
  defaultCountry = "US",
  placeholder = "Phone number",
  error,
  hint,
  required,
  disabled,
  loading,
  testID,
  icon,
  right,
  onClear,
  appearance = "default",
  countrySelector = "visible",
  countrySelectorTestID,
  countrySearchTestID,
  countrySearchPlaceholder,
  countryNoMatchesMessage,
}: Props) {
  const isGlass = appearance === "glass";
  const fontSize = useInputFontSize();
  const initial = useMemo(
    () => parseInitial(value, defaultCountry),
    [value, defaultCountry],
  );

  const [country, setCountry] = useState<CountryCode>(initial.country);
  const [display, setDisplay] = useState(() => {
    if (initial.partialInternational) return initial.partialInternational;
    if (!initial.national) return "";
    return new AsYouType(initial.country).input(initial.national);
  });
  const capturePress = usePressCapture("MoonPhoneField");
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasCountryInteraction = useRef(false);
  const phoneInputRef = useRef<TextInput | null>(null);
  // Last value this field emitted through onChange. When the parent round-
  // trips it straight back as `value`, the display is already in sync from
  // handleChange and must not be overwritten (a partial value like "+336"
  // would otherwise briefly replace the national-format display "6").
  const lastEmittedValue = useRef<string | null>(null);

  const emitChange = useCallback(
    (next: string) => {
      lastEmittedValue.current = next;
      onChange(next);
    },
    [onChange],
  );

  // Only override the user-picked country when the external value is
  // fully parseable (libphonenumber identified a country). For partial
  // input like "+336" — where the parse hasn't yet committed to a
  // specific country — keep the current selection. Otherwise the country
  // would flicker back to the fallback on every keystroke during typing,
  // re-formatting the digits under the wrong locale.
  useEffect(() => {
    if (initial.parsed) {
      setCountry(initial.country);
      setDisplay(new AsYouType(initial.country).input(initial.national));
      onCountryChange?.(initial.country);
      // The parse strips trunk prefixes (e.g. "+112125550123" →
      // "+12125550123"), so the display can silently diverge from the
      // parent's value. Re-emit the canonical E.164 so parent state always
      // matches what the field shows. Emitting only when the normalized
      // value differs from the incoming prop prevents update loops: the
      // canonical value round-trips back and parses to itself.
      if (initial.e164 && initial.e164 !== value) {
        onNationalChange?.(initial.national, initial.country);
        emitChange(initial.e164);
      }
      return;
    }
    if (initial.partialInternational) {
      // Round-trip of the field's own emission — display is already in
      // sync from handleChange; rewriting it would flash the raw
      // international string (e.g. "+336" over "6" for FR).
      if (value === lastEmittedValue.current) return;
      if (!hasCountryInteraction.current) setCountry(initial.country);
      setDisplay(initial.partialInternational);
      return;
    }
    if (!initial.national) {
      // External clear (e.g. parent reset to "").
      if (!hasCountryInteraction.current) setCountry(initial.country);
      setDisplay("");
    }
    // else: mid-typing — display is already in sync from handleChange;
    // don't override.
  }, [
    initial.parsed,
    initial.country,
    initial.national,
    initial.e164,
    initial.partialInternational,
    value,
    emitChange,
    onCountryChange,
    onNationalChange,
  ]);

  const handleChange = useCallback(
    (input: string) => {
      // Pasted international: leading "+" triggers country detection.
      if (input.includes("+")) {
        const parsed = parsePhoneNumberFromString(input);
        if (parsed?.country && parsed.nationalNumber) {
          setCountry(parsed.country);
          onCountryChange?.(parsed.country);
          onNationalChange?.(parsed.nationalNumber, parsed.country);
          const formatted = new AsYouType(parsed.country).input(
            parsed.nationalNumber,
          );
          setDisplay(formatted);
          const digits = parsed.nationalNumber.replace(/\D/g, "");
          emitChange(
            digits ? `+${getCountryCallingCode(parsed.country)}${digits}` : "",
          );
          return;
        }
        const partialInternational = input.replace(/[^\d+]/g, "");
        if (partialInternational.startsWith("+")) {
          setDisplay(partialInternational);
          onNationalChange?.("", country);
          emitChange(partialInternational);
          return;
        }
      }

      const digits = input.replace(/\D/g, "");
      const formatted = new AsYouType(country).input(digits);
      setDisplay(formatted);
      onNationalChange?.(digits, country);
      emitChange(digits ? `+${getCountryCallingCode(country)}${digits}` : "");
    },
    [country, emitChange, onCountryChange, onNationalChange],
  );

  const handleSelectCountry = useCallback(
    (next: CountryCode) => {
      hasCountryInteraction.current = true;
      setCountry(next);
      onCountryChange?.(next);
      // Re-format the existing digits under the new country's rules.
      const digits = display.replace(/\D/g, "");
      const formatted = digits ? new AsYouType(next).input(digits) : "";
      onNationalChange?.(digits, next);
      setDisplay(formatted);
      emitChange(digits ? `+${getCountryCallingCode(next)}${digits}` : "");
      setPickerOpen(false);
      // Return focus to the phone input so the user can keep typing.
      phoneInputRef.current?.focus();
    },
    [display, emitChange, onCountryChange, onNationalChange],
  );

  // Local validation, surfaced only when the caller hasn't passed `error`.
  const localError = useMemo(() => {
    if (error !== undefined) return undefined;
    const digits = display.replace(/\D/g, "");
    if (!digits) return undefined;
    const candidate = `+${getCountryCallingCode(country)}${digits}`;
    return isValidPhoneNumber(candidate) ? undefined : "Not a valid number";
  }, [display, country, error]);

  const surfacedError = error ?? localError;
  const selectedCountry = findMoonPhoneCountry(country);

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      error={surfacedError}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
      tone={isGlass ? "dark" : "default"}
    >
      <View>
        <MoonFieldInput
          hasError={!!surfacedError}
          paddingY={isGlass ? 0 : 2}
          paddingX={isGlass ? 6 : 14}
          style={
            isGlass
              ? {
                  minHeight: 58,
                  borderRadius: 18,
                  borderColor: surfacedError
                    ? colors.coral
                    : "rgba(255, 239, 225, 0.15)",
                  borderTopColor: surfacedError
                    ? colors.coral
                    : "rgba(255, 244, 236, 0.28)",
                  borderBottomColor: surfacedError
                    ? colors.coral
                    : "rgba(255, 225, 207, 0.10)",
                  backgroundColor: "transparent",
                  paddingLeft: 6,
                  paddingRight: 16,
                }
              : undefined
          }
        >
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              gap: isGlass ? 0 : 6,
              paddingVertical: isGlass ? 0 : 10,
            }}
          >
            {icon === undefined ? (
              <Phone
                size={13}
                color={isGlass ? "rgba(255, 255, 255, 0.58)" : colors.textLight}
                strokeWidth={1.5}
              />
            ) : (
              icon
            )}
            {selectedCountry ? (
              <MoonPhoneCountrySelector
                country={selectedCountry}
                onPress={() => {
                  if (disabled) return;
                  capturePress(
                    pressLabel(
                      `${selectedCountry.name} ${selectedCountry.dialCode}`,
                    ),
                    pickerOpen ? "close_country_picker" : "open_country_picker",
                  );
                  setPickerOpen((p) => !p);
                }}
                disabled={disabled}
                testID={countrySelectorTestID}
                appearance={appearance}
              />
            ) : null}
            <TextInput
              ref={phoneInputRef}
              value={display}
              onChangeText={handleChange}
              placeholder={placeholder}
              placeholderTextColor={
                isGlass ? "rgba(255, 255, 255, 0.48)" : colors.textFaint
              }
              keyboardType="phone-pad"
              inputMode="tel"
              autoComplete="tel"
              textContentType="telephoneNumber"
              autoCorrect={false}
              spellCheck={false}
              editable={!disabled}
              selectionColor={isGlass ? "rgba(255, 218, 189, 0.82)" : undefined}
              style={{
                flex: 1,
                // Web flex children default to min-width:auto, which keeps the
                // input from shrinking below its content and shoves the
                // trailing control past the bordered box. Pin it to 0 so the
                // input yields space and `right` stays inside the field.
                minWidth: 0,
                fontSize: isGlass ? 16 : fontSize,
                color: isGlass ? "rgba(255, 255, 255, 0.94)" : colors.text,
                fontFamily: "DMSans_400Regular",
                textAlign: isGlass ? "left" : undefined,
                ...(Platform.OS === "web" ? { outlineStyle: "none" } : null),
              }}
            />
            {right ? <View style={{ flexShrink: 0 }}>{right}</View> : null}
            {onClear && display ? (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel("Clear phone number"),
                    "clear_phone_number",
                  );
                  onClear();
                }}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="Clear phone number"
                // A read-only verified number can still be removable when
                // the caller explicitly provides a safe clear action (for
                // example, when another verified sign-in factor exists).
                disabled={disabled && !onClear}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  backgroundColor: isGlass
                    ? "rgba(255, 255, 255, 0.12)"
                    : `${colors.textFaint}20`,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X
                  size={11}
                  color={
                    isGlass ? "rgba(255, 255, 255, 0.72)" : colors.textFaint
                  }
                  strokeWidth={2.5}
                />
              </Pressable>
            ) : null}
          </View>
        </MoonFieldInput>

        {countrySelector === "visible" ? (
          <MoonPhoneCountryPicker
            open={pickerOpen && !disabled}
            selectedCountry={country}
            onSelect={(next) => handleSelectCountry(next.iso)}
            appearance={appearance}
            searchTestID={countrySearchTestID}
            searchPlaceholder={countrySearchPlaceholder}
            noMatchesMessage={countryNoMatchesMessage}
          />
        ) : null}
      </View>
    </MoonFormField>
  );
}
